import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useFormik } from "formik";

import { useAddOrganisation } from "../../../store";
import { validField } from "../../../valid-string";
import { OrganisationInput } from "../../../../../input-types";

interface CreateTeamModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
  open,
  onClose,
}) => {
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const formik = useFormik<OrganisationInput>({
    enableReinitialize: true,
    initialValues: {
      name: "",
      color: "#D37676",
      meetingViews: [],
    },
    validate: (values) => {
      const errors: Partial<Record<keyof typeof values, string>> = {};

      if (!validField(values.name)) {
        errors.name = "Name must not contain special characters";
      }

      if (values.name.length < 3) {
        errors.name = "Name must be at least 3 characters";
      }
      if (values.name.length > 32) {
        errors.name = "Name must be at most 32 characters";
      }

      return errors;
    },
    onSubmit: (values) => {
      addOrganisation(values);
      closeModal();
    },
  });

  const onAddSuccess = () => {
    setSnackbar({ open: true, message: "Team created successfully", severity: "success" });
  };

  const onAddError = (err: unknown) => {
    console.log(err);
    setSnackbar({ open: true, message: "Failed to create team", severity: "error" });
  };

  const { mutate: addOrganisation } = useAddOrganisation(
    onAddSuccess,
    onAddError,
  );

  const closeModal = () => {
    formik.resetForm();
    onClose();
  };

  const submitDisabled =
    !formik.isValid || !formik.dirty || formik.isSubmitting;

  return (
    <>
      <Dialog open={open} onClose={closeModal} maxWidth={"sm"} fullWidth>
        <DialogTitle component="div">
          <Typography variant="h3">Create Team</Typography>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Team Name"
                variant="outlined"
                autoComplete="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
              />
              {formik.touched.name && formik.errors.name && (
                <Typography color="error">{formik.errors.name as string}</Typography>
              )}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button fullWidth variant="outlined" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  fullWidth
                  disabled={submitDisabled}
                  type="submit"
                  variant="contained"
                >
                  Create
                </Button>
              </Stack>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message={snackbar.message}
        ContentProps={{
          sx: {
            backgroundColor: snackbar.severity === "success" ? "success.main" : "error.main",
            color: "common.white",
          },
        }}
      />
    </>
  );
};

export default CreateTeamModal;
